import { useContext, useState } from 'react';

import withMetatags from 'core/hocs/withMetatags';
import { FaqItem } from 'types/models/Faq';
import { ContactSubject } from 'types/models/ContactSubject';
import { ConfigContext } from '@providers/ConfigProvider';
import StaticLayout from '@layouts/StaticLayout';
import RenderInView from '@templates/RenderInView';
import ContactForm from '@components/ContactForm';
import Link from '@components/atoms/Link';
import Tags from '@components/Tags';
import { FormattedMessage } from 'utils/intl';
import resolveMarkups from 'utils/resolveMarkups';

import Section from './components/Section';

import classes from './Faq.module.scss';
import messages from './Faq.messages';

type Props = {
  items: FaqItem[];
  contactSubjects: ContactSubject[];
};

const formAnchor = 'contactForm';

const createId = (title: string) => encodeURI((title || '').replace(/ /g, '-').toLowerCase());

const Faq = ({ items, contactSubjects }: Props): JSX.Element => {
  const { contact } = useContext(ConfigContext);
  const [activeItemId, setActiveItemId] = useState<string>('');
  const handleAccordionClick = (id: string) => {
    if (activeItemId === id) {
      setActiveItemId('');
    } else {
      setActiveItemId(id);
    }
  };

  return (
    <StaticLayout>
      <StaticLayout.Heading>
        <StaticLayout.Header>
          <FormattedMessage {...messages.title} />
        </StaticLayout.Header>
        <StaticLayout.Description>
          <FormattedMessage
            {...messages.description}
            values={{
              link: (
                <Link className={classes.link} href={`#${formAnchor}`} underline="always">
                  <FormattedMessage {...messages.link} />
                </Link>
              ),
            }}
          />
        </StaticLayout.Description>
      </StaticLayout.Heading>
      <Tags
        items={items.map(({ id, title }) => {
          const resolvedTitle = resolveMarkups(title, contact);

          return { id, name: resolvedTitle, href: `#${createId(resolvedTitle)}` };
        })}
      />
      <div className={classes.sectionsContainer}>
        {items.map(({ id, title, questions }) => {
          const resolvedTitle = resolveMarkups(title, contact);

          return (
            <Section
              key={id}
              id={createId(resolvedTitle)}
              title={resolvedTitle}
              questions={questions}
              activeId={activeItemId}
              onItemClick={handleAccordionClick}
            />
          );
        })}
      </div>
      <RenderInView>
        {({ ref, inView }) => (
          <div ref={ref} id={formAnchor} className={classes.formWrapper}>
            {inView && (
              <ContactForm
                header={<FormattedMessage {...messages.formHeader} />}
                description={<FormattedMessage {...messages.formDescription} />}
                contactSubjects={contactSubjects}
              />
            )}
          </div>
        )}
      </RenderInView>
    </StaticLayout>
  );
};

export default withMetatags(Faq, { withCanonicalUrl: true });
